<template>
  <div class="report">
    <!-- ── Date controls (apply to everything) ─────────────────── -->
    <div class="report__controls m4a-card">
      <h2 class="report__title">Production Report</h2>
      <div class="report__range">
        <div class="preset">
          <button
            v-for="p in presets"
            :key="p.days"
            class="preset__btn"
            :class="{ 'preset__btn--active': activePreset === p.days }"
            @click="applyPreset(p.days)"
          >
            {{ p.label }}
          </button>
        </div>
        <label class="date-field">
          <span>From</span>
          <input type="date" class="field" v-model="fromDate" :max="toDate" @change="onDateChange" />
        </label>
        <label class="date-field">
          <span>To</span>
          <input type="date" class="field" v-model="toDate" :max="todayISO" @change="onDateChange" />
        </label>
        <button class="btn-info btn-sm apply-btn" @click="loadReport">Apply</button>
      </div>
    </div>
    <p v-if="rangeNote" class="range-note">{{ rangeNote }}</p>

    <div v-if="loading" class="m4a-card state">
      <span class="spinner" /> Loading factory production for {{ rangeLabel }}…
    </div>

    <template v-else>
      <!-- ══ FACTORY SUMMARY ══════════════════════════════════ -->
      <h3 class="section-title">Factory Summary <span>{{ rangeLabel }}</span></h3>

      <div class="kpis">
        <div class="kpi kpi--accent">
          <div class="kpi__label">Total Meters</div>
          <div class="kpi__value">{{ fmt(summary.totalMeters) }}<span class="kpi__unit">m</span></div>
          <div class="kpi__sub">Extruders + Corrugators</div>
        </div>
        <div class="kpi kpi--blue">
          <div class="kpi__label">Total Units</div>
          <div class="kpi__value">{{ fmt(summary.totalUnits) }}<span class="kpi__unit">u</span></div>
          <div class="kpi__sub">Compression + Injection</div>
        </div>
        <div class="kpi kpi--green">
          <div class="kpi__label">Producing Machines</div>
          <div class="kpi__value">{{ summary.producing }}</div>
          <div class="kpi__sub">of {{ allMachines.length }} total</div>
        </div>
        <div class="kpi kpi--amber">
          <div class="kpi__label">Daily Avg (m)</div>
          <div class="kpi__value">{{ fmt(summary.totalMeters / numDays) }}</div>
          <div class="kpi__sub">meters / day</div>
        </div>
      </div>

      <div class="grid-2">
        <div class="m4a-card chartcard">
          <div class="chartcard__head"><h3>Factory Daily Production</h3></div>
          <div class="chartcard__body chartcard__body--chart"><canvas ref="factory"></canvas></div>
        </div>
        <div class="m4a-card chartcard">
          <div class="chartcard__head"><h3>Output by Machine Type</h3></div>
          <div class="chartcard__body type-cards">
            <button
              v-for="t in typeSummaries"
              :key="t.value"
              class="type-card"
              :class="{ 'type-card--active': selectedType === t.value }"
              @click="selectType(t.value)"
            >
              <span class="type-card__name">{{ t.label }}</span>
              <span class="type-card__value">{{ fmt(t.total) }} <small>{{ t.unit }}</small></span>
              <span class="type-card__sub">{{ t.machines }} producing</span>
            </button>
          </div>
        </div>
      </div>

      <!-- ══ PER MACHINE ══════════════════════════════════════ -->
      <h3 class="section-title">
        Per Machine · {{ selectedLabel }}
        <span>{{ selectedUnit }}</span>
      </h3>

      <div class="m4a-card chartcard">
        <div class="chartcard__head">
          <h3>Output by Machine</h3>
          <json-excel :data="excelData" :name="excelName" class="btn-info btn-sm export-btn">
            Export
          </json-excel>
        </div>
        <div class="chartcard__body"><canvas ref="byMachine" :height="byMachineHeight"></canvas></div>
      </div>

      <div class="m4a-card tablecard">
        <table class="rtable">
          <thead>
            <tr>
              <th>#</th><th>Machine</th><th>Current product</th>
              <th class="num">{{ selectedUnit }}</th><th class="num">Daily avg</th><th class="num">Share</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(m, i) in selectedMachines" :key="m.name">
              <td>{{ i + 1 }}</td>
              <td class="strong">{{ m.name }}</td>
              <td class="muted">{{ m.product }}</td>
              <td class="num strong">{{ fmt(m.total) }}</td>
              <td class="num muted">{{ fmt(m.total / numDays) }}</td>
              <td class="num">
                <div class="share">
                  <div class="share__bar" :style="{ width: sharePct(m.total) + '%' }"></div>
                  <span>{{ sharePct(m.total) }}%</span>
                </div>
              </td>
            </tr>
            <tr v-if="!selectedMachines.length">
              <td colspan="6" class="empty">No production recorded for {{ selectedLabel.toLowerCase() }} in this range.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import Chart from 'chart.js'
import JsonExcel from 'vue-json-excel'

const BASE = 'https://m4a.ramacsystems.com/backend'
const WEEKLY_URL = BASE + '/mechines/machine_charts/compressionWeekly.php'
const MACHINES_URL = BASE + '/mechines/addGet.php'
const MAX_DAYS = 30

const TYPES = [
  { value: 'Extruder', label: 'Extruders', family: 'distance', unit: 'm' },
  { value: 'Compression Mould', label: 'Compression', family: 'count', unit: 'u' },
  { value: 'Injection Mould', label: 'Injection', family: 'count', unit: 'u' },
  { value: 'Corrugator', label: 'Corrugators', family: 'distance', unit: 'm' },
]

function iso(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

export default {
  name: 'ProductionReport',
  components: { JsonExcel },

  data() {
    const today = new Date()
    const weekAgo = new Date()
    weekAgo.setDate(weekAgo.getDate() - 6)
    return {
      presets: [
        { days: 7, label: '7 days' },
        { days: 14, label: '14 days' },
        { days: 30, label: '30 days' },
      ],
      selectedType: 'Extruder',
      fromDate: iso(weekAgo),
      toDate: iso(today),
      activePreset: 7,
      allMachines: [],
      machineData: [], // { name, type, family, unit, product, total, daily:{date:val} }
      loading: false,
      rangeNote: '',
      factoryChart: null,
      machineChart: null,
    }
  },

  computed: {
    todayISO() {
      return iso(new Date())
    },
    numDays() {
      const a = new Date(this.fromDate)
      const b = new Date(this.toDate)
      return Math.max(1, Math.round((b - a) / 86400000) + 1)
    },
    rangeLabel() {
      return `${this.fromDate} → ${this.toDate}`
    },
    dateRange() {
      const labels = []
      const start = new Date(this.fromDate)
      for (let i = 0; i < this.numDays; i++) {
        const d = new Date(start)
        d.setDate(d.getDate() + i)
        labels.push(iso(d))
      }
      return labels
    },
    summary() {
      let totalMeters = 0
      let totalUnits = 0
      let producing = 0
      for (const m of this.machineData) {
        if (m.total > 0) producing++
        if (m.family === 'distance') totalMeters += m.total
        else totalUnits += m.total
      }
      return { totalMeters, totalUnits, producing }
    },
    typeSummaries() {
      return TYPES.map((t) => {
        const list = this.machineData.filter((m) => m.type === t.value)
        return {
          ...t,
          total: list.reduce((s, m) => s + m.total, 0),
          machines: list.filter((m) => m.total > 0).length,
        }
      })
    },
    selectedMeta() {
      return TYPES.find((t) => t.value === this.selectedType) || TYPES[0]
    },
    selectedLabel() {
      return this.selectedMeta.label
    },
    selectedUnit() {
      return this.selectedMeta.family === 'distance' ? 'Meters' : 'Units'
    },
    selectedMachines() {
      return this.machineData
        .filter((m) => m.type === this.selectedType && m.total > 0)
        .sort((a, b) => b.total - a.total)
    },
    selectedTotal() {
      return this.selectedMachines.reduce((s, m) => s + m.total, 0)
    },
    byMachineHeight() {
      return Math.max(120, this.selectedMachines.length * 30 + 30)
    },
    excelName() {
      return `production_${this.selectedType}_${this.fromDate}_${this.toDate}.xls`
    },
    excelData() {
      return this.selectedMachines.map((m) => ({
        Machine: m.name,
        Product: m.product,
        [this.selectedUnit]: Number(m.total.toFixed(2)),
        DailyAverage: Number((m.total / this.numDays).toFixed(2)),
      }))
    },
  },

  mounted() {
    this.loadMachines()
  },

  beforeDestroy() {
    if (this.factoryChart) this.factoryChart.destroy()
    if (this.machineChart) this.machineChart.destroy()
  },

  methods: {
    token(name) {
      return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    },
    fmt(v) {
      const n = Number(v || 0)
      return n.toLocaleString(undefined, { maximumFractionDigits: n >= 100 ? 0 : 2 })
    },
    sharePct(v) {
      if (!this.selectedTotal) return 0
      return Math.round((v / this.selectedTotal) * 100)
    },

    async loadMachines() {
      try {
        const res = await axios.get(MACHINES_URL, { params: { params: '', action: 'getMachines' } })
        this.allMachines = Array.isArray(res.data) ? res.data : []
      } catch (e) {
        this.allMachines = []
      }
      this.loadReport()
    },

    selectType(value) {
      this.selectedType = value
      this.$nextTick(this.renderMachineChart)
    },

    applyPreset(days) {
      this.activePreset = days
      const today = new Date()
      const from = new Date()
      from.setDate(from.getDate() - (days - 1))
      this.fromDate = iso(from)
      this.toDate = iso(today)
      this.rangeNote = ''
      this.loadReport()
    },

    onDateChange() {
      this.activePreset = null
      const a = new Date(this.fromDate)
      const b = new Date(this.toDate)
      if (b < a) {
        this.fromDate = this.toDate
        return
      }
      const days = Math.round((b - a) / 86400000) + 1
      if (days > MAX_DAYS) {
        const clamped = new Date(b)
        clamped.setDate(clamped.getDate() - (MAX_DAYS - 1))
        this.fromDate = iso(clamped)
        this.rangeNote = `Range limited to a maximum of ${MAX_DAYS} days.`
      } else {
        this.rangeNote = ''
      }
    },

    async loadReport() {
      const machines = this.allMachines.filter((m) => TYPES.some((t) => t.value === m.type))
      if (!machines.length) {
        this.machineData = []
        return
      }
      this.loading = true
      const from = this.fromDate + ' 00:00:00'
      const to = this.toDate + ' 23:59:59'
      try {
        const results = await Promise.all(
          machines.map((m) =>
            axios
              .get(WEEKLY_URL, {
                params: {
                  ramac_device: m.ramacDevice,
                  from_date: from,
                  to_date: to,
                  PacketType: m.MonitorInput,
                },
              })
              .then((r) => ({ machine: m, rows: Array.isArray(r.data) ? r.data : [] }))
              .catch(() => ({ machine: m, rows: [] })),
          ),
        )
        this.aggregate(results)
      } finally {
        this.loading = false
        this.$nextTick(this.renderCharts)
      }
    },

    aggregate(results) {
      const out = []
      for (const { machine, rows } of results) {
        const meta = TYPES.find((t) => t.value === machine.type)
        if (!meta) continue
        const distance = meta.family === 'distance'
        const cfg = Number(machine.pulseConfig) || 1
        const daily = {}
        let total = 0
        for (const row of rows) {
          const date = row.DateUTC || (row.DateTimeUTC || '').slice(0, 10)
          if (!date) continue
          let value = 0
          if (distance) {
            const dp = Number(row.deltaPulses || 0)
            value = dp > 0 ? dp / cfg : 0
          } else {
            value = Number(row.DigitalState) === 1 ? 0.5 : 0
          }
          if (!value) continue
          daily[date] = (daily[date] || 0) + value
          total += value
        }
        out.push({
          name: machine.machineName,
          type: machine.type,
          family: meta.family,
          unit: meta.unit,
          product: machine.product,
          total,
          daily,
        })
      }
      this.machineData = out
    },

    renderCharts() {
      this.renderFactoryChart()
      this.renderMachineChart()
    },

    axisText() {
      return this.token('--text-muted') || '#9aa7c2'
    },
    gridColor() {
      return this.token('--chart-grid') || 'rgba(255,255,255,0.08)'
    },

    renderFactoryChart() {
      if (!this.$refs.factory) return
      if (this.factoryChart) this.factoryChart.destroy()
      const labels = this.dateRange
      const meters = labels.map((d) =>
        Number(
          this.machineData
            .filter((m) => m.family === 'distance')
            .reduce((s, m) => s + (m.daily[d] || 0), 0)
            .toFixed(2),
        ),
      )
      const units = labels.map((d) =>
        Number(
          this.machineData
            .filter((m) => m.family === 'count')
            .reduce((s, m) => s + (m.daily[d] || 0), 0)
            .toFixed(2),
        ),
      )
      const green = this.token('--accent') || '#34d27b'
      const blue = this.token('--chart-2') || '#5b8def'
      const text = this.axisText()
      const grid = this.gridColor()
      const ctx = this.$refs.factory.getContext('2d')
      this.factoryChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels.map((d) => d.slice(5)),
          datasets: [
            {
              label: 'Meters',
              data: meters,
              yAxisID: 'm',
              borderColor: green,
              backgroundColor: this.hexA(green, 0.12),
              pointBackgroundColor: green,
              borderWidth: 2,
              lineTension: 0.3,
              fill: true,
            },
            {
              label: 'Units',
              data: units,
              yAxisID: 'u',
              borderColor: blue,
              backgroundColor: this.hexA(blue, 0.1),
              pointBackgroundColor: blue,
              borderWidth: 2,
              lineTension: 0.3,
              fill: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { labels: { fontColor: text } },
          tooltips: { mode: 'index', intersect: false },
          scales: {
            xAxes: [{ gridLines: { color: 'transparent' }, ticks: { fontColor: text, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } }],
            yAxes: [
              { id: 'm', position: 'left', gridLines: { color: grid }, ticks: { fontColor: text, beginAtZero: true }, scaleLabel: { display: true, labelString: 'Meters', fontColor: text } },
              { id: 'u', position: 'right', gridLines: { display: false }, ticks: { fontColor: text, beginAtZero: true }, scaleLabel: { display: true, labelString: 'Units', fontColor: text } },
            ],
          },
        },
      })
    },

    renderMachineChart() {
      if (!this.$refs.byMachine) return
      if (this.machineChart) this.machineChart.destroy()
      const list = this.selectedMachines
      const color = this.selectedMeta.family === 'distance' ? this.token('--accent') : this.token('--chart-2')
      const text = this.axisText()
      const grid = this.gridColor()
      const ctx = this.$refs.byMachine.getContext('2d')
      this.machineChart = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels: list.map((m) => m.name),
          datasets: [
            {
              label: this.selectedUnit,
              data: list.map((m) => Number(m.total.toFixed(2))),
              backgroundColor: this.hexA(color || '#34d27b', 0.75),
              hoverBackgroundColor: color,
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: { mode: 'index', intersect: false },
          scales: {
            xAxes: [{ gridLines: { color: grid }, ticks: { fontColor: text, beginAtZero: true } }],
            yAxes: [{ gridLines: { color: 'transparent' }, ticks: { fontColor: text } }],
          },
        },
      })
    },

    hexA(hex, a) {
      const h = (hex || '#34d27b').replace('#', '')
      const n = parseInt(h.length === 3 ? h.replace(/(.)/g, '$1$1') : h, 16)
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
    },
  },
}
</script>

<style scoped>
.report {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Controls */
.report__controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
  padding: 12px 18px;
}
.report__title { font-size: 17px; }
.report__range {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.preset { display: flex; gap: 4px; align-self: center; }
.preset__btn {
  border: 1px solid var(--border);
  background: var(--bg-base);
  color: var(--text-muted);
  padding: 6px 10px;
  border-radius: var(--radius-sm);
  font-size: 12px;
  font-weight: 600;
}
.preset__btn--active { border-color: var(--accent); color: var(--accent); }
.date-field {
  display: flex;
  flex-direction: column;
  font-size: 11px;
  color: var(--text-muted);
  gap: 2px;
}
.date-field .field { width: 150px; padding: 6px 8px; }
.range-note { color: var(--status-warning); font-size: 12px; margin: -6px 2px 0; }

/* Section titles */
.section-title {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin: 6px 0 -4px;
}
.section-title span { color: var(--text-faint); margin-left: 8px; text-transform: none; letter-spacing: 0; }

/* KPIs */
.kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.kpi {
  background: var(--surface);
  border: 1px solid var(--border);
  border-left: 3px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 10px 14px;
}
.kpi--accent { border-left-color: var(--accent); }
.kpi--blue { border-left-color: var(--chart-2); }
.kpi--green { border-left-color: var(--status-running); }
.kpi--amber { border-left-color: var(--status-warning); }
.kpi__label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); }
.kpi__value { margin-top: 2px; font-size: 22px; font-weight: 700; line-height: 1.1; }
.kpi__unit { margin-left: 4px; font-size: 12px; font-weight: 500; color: var(--text-muted); }
.kpi__sub { margin-top: 2px; font-size: 11px; color: var(--text-faint); }

/* Layout */
.grid-2 { display: grid; grid-template-columns: 1.3fr 1fr; gap: 16px; }

/* Cards */
.chartcard__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid var(--border);
}
.chartcard__head h3 { font-size: 15px; }
.chartcard__body { padding: 16px 18px; }
.chartcard__body--chart { height: 290px; position: relative; }
.chartcard__body--chart canvas { max-height: 100%; }
.export-btn { cursor: pointer; }

/* Type cards (also the per-machine selector) */
.type-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 14px 16px; align-content: start; }
.type-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  background: var(--bg-base);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 9px 12px;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.type-card:hover { background: var(--surface-2); }
.type-card--active { border-color: var(--accent); background: var(--accent-soft); }
.type-card__name { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.03em; }
.type-card__value { font-size: 17px; font-weight: 700; }
.type-card__value small { font-size: 11px; font-weight: 500; color: var(--text-muted); }
.type-card__sub { font-size: 11px; color: var(--text-faint); }

.state { display: flex; align-items: center; gap: 12px; padding: 22px; color: var(--text-muted); }
.spinner { width: 18px; height: 18px; border: 2px solid var(--border-strong); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Table */
.tablecard { padding: 6px 0; }
.rtable { width: 100%; border-collapse: collapse; font-size: 13px; }
.rtable th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; color: var(--text-muted); padding: 10px 16px; border-bottom: 1px solid var(--border); }
.rtable td { padding: 9px 16px; border-bottom: 1px solid var(--border); }
.rtable tbody tr:hover { background: var(--surface-2); }
.rtable .num { text-align: right; font-variant-numeric: tabular-nums; }
.rtable .strong { font-weight: 650; }
.rtable .empty { text-align: center; color: var(--text-muted); padding: 22px; }
.share { display: flex; align-items: center; gap: 8px; justify-content: flex-end; }
.share__bar { height: 6px; border-radius: 999px; background: var(--accent); min-width: 2px; max-width: 90px; }

@media (max-width: 1100px) {
  .kpis { grid-template-columns: repeat(2, 1fr); }
  .grid-2 { grid-template-columns: 1fr; }
  .type-cards { grid-template-columns: 1fr 1fr; }
  .report__controls { align-items: flex-start; flex-direction: column; }
}
</style>
